// src/screens/ResetPasswordScreen.js
// The reset page (FORGOT_PASSWORD_DESIGN.md 4.2; strings RP1 to RP18):
// critcash.com/reset-password?code=<oobCode>, the link in OUR reset email
// (sendPasswordReset, PE2). Never Firebase's stock action page.
//
// The code is read once into memory and dropped from the address bar as soon
// as the page has loaded (until it is used, anyone who sees it could set the
// password; streamers capture their screens live). While the app boots
// (bundle, then auth restore) the clicked link still shows in the address
// bar, as it does during any navigation (review R2-01). Then:
//   checking: verifyPasswordResetCode -> the account's address, shown masked
//     only (RP3). An expired, used or unknown code -> used (RP14); a disabled
//     account -> RestoreAccount; anything else -> failed (RP16, Try again).
//   form: new password + confirm, the signup rules and lines word for word;
//     a hidden username field (the account's address) so password managers
//     update the right saved login; RP20 when this browser is signed in to a
//     different account.
//   saving: confirmPasswordReset (Firebase sets the password, marks the email
//     confirmed and ends older sessions).
//   finishing: the password IS saved (RP19 shows at once), then finishReset:
//     sign in, call endOtherSessions (every other browser and device loses
//     its session within the hour; it also answers what a new password leaves
//     in place), sign in again. With a different account signed in here,
//     nothing is signed in (RP11). Once the password is saved every outcome
//     says so (RP9 / RP10 / RP11), whatever fails after it, followed by what
//     a new password does not remove (RP21 / RP22 / RP23).
//   nocode: no code in memory (a reload after the drop, or a bare URL): RP18.
// Focus moves to the result container whenever the phase changes (the
// focused button unmounts), so a screen reader reads every line in it (RP9
// with RP21 to RP23; review R2-03); results are role="status", errors
// role="alert". The New password field is described by RP3, RP20, its hint
// (RP6) and, while shown, its error, so they are read when it takes focus
// (reviews R2-11, R3-04); the Confirm field by its error. A field with an
// error is aria-invalid.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebaseConfig';
import Footer from '../components/Footer';
import { maskEmail } from '../utils/membershipJoin';
import {
  RESET_TITLE,
  CHECKING_LINE,
  forAccountLine,
  OTHER_ACCOUNT_LINE,
  FINISHING_LINE,
  NEW_PASSWORD_LABEL,
  CONFIRM_PASSWORD_LABEL,
  PASSWORD_HINT,
  SAVE_PASSWORD_LABEL,
  SAVE_FAILED_LINE,
  PASSWORD_WEAK_LINE,
  BACK_TO_CRITCAST_LABEL,
  SIGN_IN_LABEL,
  RESET_LINK_USED_LINE,
  GET_NEW_LINK_LABEL,
  CHECK_FAILED_LINE,
  TRY_AGAIN_LABEL,
  NO_CODE_LINE,
  newPasswordErrors,
  readResetCode,
  verifyErrorKind,
  confirmErrorKind,
  otherAccountSignedIn,
  finishReset,
  doneLine,
  doneExtraLines,
} from '../utils/passwordReset';

const COLORS = {
  BG: '#000',
  CARD: '#111',
  BORDER: '#222',
  GOLD: '#FCD000',
  GREEN: '#2ECC71',
  TEXT: '#EDEDED',
  DIM: '#aaa',
  RED: '#e05555',
  FIELD: '#1b1b1b',
};

export default function ResetPasswordScreen({ navigation, route }) {
  // Read once: the code leaves the address bar as soon as the page has loaded.
  const codeRef = useRef(null);
  if (codeRef.current === null) codeRef.current = readResetCode(route?.params);
  const code = codeRef.current;

  // checking | form | saving | finishing | done | used | failed | nocode
  const [phase, setPhase] = useState(code ? 'checking' : 'nocode');
  const [masked, setMasked] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [outcome, setOutcome] = useState(null);
  const [others, setOthers] = useState(null);
  const [viewer, setViewer] = useState(() => auth.currentUser);
  const emailRef = useRef('');
  const submittingRef = useRef(false);
  const ranRef = useRef(false);
  const aliveRef = useRef(true);
  const confirmRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Who is signed in on this browser (RP20, and finishReset's step 0).
  useEffect(() => {
    try {
      return onAuthStateChanged(auth, (u) => { if (aliveRef.current) setViewer(u); });
    } catch (_) {
      return undefined;
    }
  }, []);

  // The focused button unmounts when the phase changes: move focus to the
  // result container so keyboard and screen-reader users keep their place.
  useEffect(() => {
    if (!['finishing', 'done', 'used', 'failed', 'nocode'].includes(phase)) return;
    try { if (resultRef.current && resultRef.current.focus) resultRef.current.focus(); } catch (_) { /* noop */ }
  }, [phase, outcome]);

  // Drop the code from the address bar once the page has mounted (it stays in
  // memory only).
  useEffect(() => {
    if (route?.params?.code !== undefined) {
      try { navigation.setParams({ code: undefined }); } catch (_) { /* noop */ }
    }
  }, [route?.params?.code, navigation]);

  const check = useCallback(async () => {
    if (!code) {
      setPhase('nocode');
      return;
    }
    setPhase('checking');
    try {
      const email = await verifyPasswordResetCode(auth, code);
      if (!aliveRef.current) return;
      emailRef.current = typeof email === 'string' ? email : '';
      setMasked(maskEmail(emailRef.current) || '');
      setPhase('form');
    } catch (err) {
      if (!aliveRef.current) return;
      const kind = verifyErrorKind(err);
      if (kind === 'disabled') {
        navigation.replace('RestoreAccount');
        return;
      }
      if (kind === 'retry') console.warn('[reset-password] verifyPasswordResetCode failed:', err?.code || err?.message);
      setPhase(kind === 'used' ? 'used' : 'failed');
    }
  }, [code, navigation]);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    check();
  }, [check]);

  const submit = async () => {
    if (submittingRef.current || phase !== 'form') return;
    const errs = newPasswordErrors(password, confirm);
    setErrors(errs);
    setFormError('');
    if (Object.keys(errs).length) return;
    submittingRef.current = true;
    const pw = password;
    setPhase('saving');
    try {
      await confirmPasswordReset(auth, code, pw);
    } catch (err) {
      submittingRef.current = false;
      if (!aliveRef.current) return;
      const kind = confirmErrorKind(err);
      if (kind === 'disabled') {
        navigation.replace('RestoreAccount');
        return;
      }
      if (kind === 'used') {
        setPassword('');
        setConfirm('');
        setPhase('used');
        return;
      }
      setPhase('form');
      if (kind === 'weak') {
        setErrors({ password: PASSWORD_WEAK_LINE });
      } else {
        console.warn('[reset-password] confirmPasswordReset failed:', err?.code || err?.message);
        setFormError(SAVE_FAILED_LINE);
      }
      return;
    }
    // The password IS saved: the fields empty now, and every outcome says so
    // (RP19 at once while it signs in, then the done lines). With another
    // account signed in here nothing is signed in, so RP19 never shows.
    const elsewhere = otherAccountSignedIn(auth.currentUser, emailRef.current);
    if (aliveRef.current) {
      setPassword('');
      setConfirm('');
      if (!elsewhere) setPhase('finishing');
    }
    const result = await finishReset({
      email: emailRef.current,
      password: pw,
      signedInElsewhere: elsewhere,
      signIn: (e, p) => signInWithEmailAndPassword(auth, e, p),
      endOtherSessions: () => httpsCallable(functions, 'endOtherSessions')(),
      signOut: () => signOut(auth),
    });
    if (!aliveRef.current) return;
    setOthers(result.others);
    setOutcome(result.outcome);
    setPhase('done');
  };

  const button = (label, onPress, kind = 'gold', busy = false) => (
    <TouchableOpacity
      style={[kind === 'gold' ? styles.goldBtn : styles.outlineBtn, busy ? styles.btnBusy : null]}
      onPress={onPress}
      disabled={busy}
      accessibilityRole="button"
      accessibilityState={{ disabled: busy, busy }}
    >
      {busy ? <ActivityIndicator color="#0b0b0b" style={styles.btnSpinner} /> : null}
      <Text style={kind === 'gold' ? styles.goldBtnText : styles.outlineBtnText}>{label}</Text>
    </TouchableOpacity>
  );
  const getNewLink = () => navigation.replace('ForgotPassword');
  const goSignIn = () => navigation.replace('Login');
  // A result line. Its container (ref={resultRef}, tabIndex -1: focusable from
  // code only, never a tab stop) takes focus when the phase changes.
  const resultLine = (text, extra) => <Text style={[styles.body, extra]}>{text}</Text>;

  const renderForm = () => {
    const saving = phase === 'saving';
    const elsewhere = otherAccountSignedIn(viewer, emailRef.current);
    // RP3, RP20, the hint and the error describe the New password field (it
    // takes focus first); the Confirm field is described by its error.
    const describedBy = [masked ? 'reset-for-account' : null, elsewhere ? 'reset-other-account' : null, 'reset-password-hint', errors.password ? 'reset-password-error' : null].filter(Boolean).join(' ');
    return (
      <>
        {masked ? <Text id="reset-for-account" style={styles.body}>{forAccountLine(masked)}</Text> : null}
        {elsewhere ? <Text id="reset-other-account" style={[styles.body, styles.note]}>{OTHER_ACCOUNT_LINE}</Text> : null}

        {/* The account's address for password managers (they save the new
            password under it); never shown, never read aloud. */}
        <TextInput
          value={emailRef.current}
          editable={false}
          autoComplete="username"
          textContentType="username"
          autoCapitalize="none"
          autoCorrect={false}
          aria-hidden
          tabIndex={-1}
          style={styles.hiddenUsername}
        />

        <Text style={styles.label}>{NEW_PASSWORD_LABEL}</Text>
        <TextInput
          style={[styles.input, errors.password ? styles.inputErr : null]}
          value={password}
          onChangeText={setPassword}
          onSubmitEditing={() => (confirmRef.current ? confirmRef.current.focus() : submit())}
          accessibilityLabel={NEW_PASSWORD_LABEL}
          aria-describedby={describedBy}
          aria-invalid={!!errors.password}
          secureTextEntry
          autoComplete="new-password"
          textContentType="newPassword"
          autoCapitalize="none"
          autoCorrect={false}
          autoFocus
          returnKeyType="next"
          editable={!saving}
        />
        <Text id="reset-password-hint" style={styles.hint}>{PASSWORD_HINT}</Text>
        <View role="alert">
          {errors.password ? <Text id="reset-password-error" style={styles.fieldErr}>{errors.password}</Text> : null}
        </View>

        <Text style={styles.label}>{CONFIRM_PASSWORD_LABEL}</Text>
        <TextInput
          ref={confirmRef}
          style={[styles.input, errors.confirm ? styles.inputErr : null]}
          value={confirm}
          onChangeText={setConfirm}
          onSubmitEditing={submit}
          accessibilityLabel={CONFIRM_PASSWORD_LABEL}
          aria-describedby={errors.confirm ? 'reset-confirm-error' : undefined}
          aria-invalid={!!errors.confirm}
          secureTextEntry
          autoComplete="new-password"
          textContentType="newPassword"
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="done"
          editable={!saving}
        />
        <View role="alert">
          {errors.confirm ? <Text id="reset-confirm-error" style={styles.fieldErr}>{errors.confirm}</Text> : null}
        </View>

        {button(SAVE_PASSWORD_LABEL, submit, 'gold', saving)}
        <View role="alert">
          {formError ? <Text style={[styles.body, styles.err]}>{formError}</Text> : null}
        </View>
      </>
    );
  };

  const renderBody = () => {
    if (phase === 'checking') {
      return (
        <View style={styles.row}>
          <ActivityIndicator color={COLORS.GOLD} style={styles.inlineSpinner} />
          <Text style={styles.body}>{CHECKING_LINE}</Text>
        </View>
      );
    }
    if (phase === 'form' || phase === 'saving') return renderForm();
    if (phase === 'finishing') {
      return (
        <View ref={resultRef} tabIndex={-1} style={styles.row} role="status">
          <ActivityIndicator color={COLORS.GOLD} style={styles.inlineSpinner} />
          {resultLine(FINISHING_LINE, styles.ok)}
        </View>
      );
    }
    if (phase === 'done') {
      const signedIn = outcome === 'all' || outcome === 'signed_in';
      // Another account is signed in here: RP3 names the account the new
      // password is for, so RP23's "this account" has a referent (review R2-06).
      return (
        <>
          <View ref={resultRef} tabIndex={-1} role="status">
            {outcome === 'other_account' && masked ? <Text style={styles.body}>{forAccountLine(masked)}</Text> : null}
            {resultLine(doneLine(outcome, masked), styles.ok)}
            {doneExtraLines(outcome, others).map((line) => (
              <Text key={line} style={[styles.body, styles.note]}>{line}</Text>
            ))}
          </View>
          {signedIn
            ? button(BACK_TO_CRITCAST_LABEL, () => navigation.replace('Home'))
            : button(SIGN_IN_LABEL, goSignIn)}
        </>
      );
    }
    if (phase === 'used') {
      return (
        <>
          <View ref={resultRef} tabIndex={-1} role="status">{resultLine(RESET_LINK_USED_LINE)}</View>
          {button(SIGN_IN_LABEL, goSignIn)}
          {button(GET_NEW_LINK_LABEL, getNewLink, 'outline')}
        </>
      );
    }
    if (phase === 'failed') {
      return (
        <>
          <View ref={resultRef} tabIndex={-1} role="alert">{resultLine(CHECK_FAILED_LINE, styles.err)}</View>
          {button(TRY_AGAIN_LABEL, check)}
          {button(GET_NEW_LINK_LABEL, getNewLink, 'outline')}
        </>
      );
    }
    return (
      <>
        <View ref={resultRef} tabIndex={-1} role="status">{resultLine(NO_CODE_LINE)}</View>
        {button(GET_NEW_LINK_LABEL, getNewLink)}
      </>
    );
  };

  return (
    <View style={styles.outer}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.card}>
          <Text style={styles.title} accessibilityRole="header">{RESET_TITLE}</Text>
          {renderBody()}
        </View>
      </ScrollView>
      <Footer navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  outer: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  card: {
    width: '100%', maxWidth: 440, backgroundColor: COLORS.CARD, borderColor: COLORS.BORDER,
    borderWidth: 1, borderRadius: 14, padding: 22,
  },
  title: { color: COLORS.GOLD, fontSize: 22, fontWeight: '800', textAlign: 'center', marginBottom: 12 },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 8 },
  inlineSpinner: { marginRight: 10 },
  body: { color: COLORS.TEXT, fontSize: 14.5, lineHeight: 21, textAlign: 'center', marginTop: 4 },
  ok: { color: COLORS.GREEN, fontWeight: '700' },
  err: { color: COLORS.RED, marginTop: 12 },
  note: { color: COLORS.DIM, marginTop: 10 },
  hiddenUsername: { display: 'none' },
  label: { color: COLORS.TEXT, fontSize: 13.5, fontWeight: '700', marginTop: 16, marginBottom: 6 },
  hint: { color: COLORS.DIM, fontSize: 12.5, marginTop: 6 },
  input: {
    backgroundColor: COLORS.FIELD, color: '#fff', borderColor: COLORS.BORDER, borderWidth: 1, borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, minHeight: 44,
  },
  inputErr: { borderColor: COLORS.RED },
  fieldErr: { color: COLORS.RED, fontSize: 13, marginTop: 6 },
  goldBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12, minHeight: 44, marginTop: 18,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
  },
  btnBusy: { opacity: 0.75 },
  btnSpinner: { marginRight: 8 },
  goldBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
  outlineBtn: {
    borderColor: COLORS.GOLD, borderWidth: 1, borderRadius: 10, paddingVertical: 11, minHeight: 44, marginTop: 10,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
  },
  outlineBtnText: { color: COLORS.GOLD, fontSize: 14, fontWeight: '800' },
});
